'use client';

import {Button} from '@/components/ui/button';
import {Checkbox} from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {useToast} from '@/hooks/use-toast';
import {trackEvent} from '@/lib/gtm';
import {LeadSchema, PostLead} from '@/types/form';
import {zodResolver} from '@hookform/resolvers/zod';
import {LoaderCircle} from 'lucide-react';
import Link from 'next/link';
import {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import Send from '@/assets/icons/Send.svg';
import SectionHeader from '@/app/(website)/components/SectionHeader';

export default function ContactUs({
  onSubmitSucceeded,
}: {
  onSubmitSucceeded?: () => void;
}) {
  const {toast} = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
      },
    );

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    return () => {
      if (formRef.current) {
        observer.unobserve(formRef.current);
      }
    };
  }, []);

  const form = useForm<PostLead>({
    resolver: zodResolver(LeadSchema),
  });

  const encode = (data: any) => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]),
      )
      .join('&');
  };

  async function onSubmit(data: PostLead) {
    try {
      trackEvent('formSubscription', data);
      const payload = {
        'entry.1281100226': data.name,
        'entry.1956414444': data.surname,
        'entry.2074188680': data.company,
        'entry.1892945232': data.role,
        'entry.1225562881': data.email,
        'entry.854530408': data.phone,
      };

      await fetch(
        'https://docs.google.com/forms/u/1/d/e/1FAIpQLSdt2WGpdLHcYy8mXiOHOdoazwLRQdzfYcZ-9rreEFZ4Kpgn-A/formResponse',
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          method: 'POST',
          mode: 'no-cors',
          body: encode(payload),
        },
      );

      setIsSubmitted(true);
      if (onSubmitSucceeded) {
        onSubmitSucceeded();
      }
    } catch (err) {
      console.error(err);
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore',
        variant: 'destructive',
      });
    }
  }

  return (
    <div id="contact-us" className="w-full md:px-8 py-16">
      <div className="bg-background-2 flex flex-col items-center justify-center md:rounded-2xl px-8 md:px-0 py-16 gap-16">
        <SectionHeader
          badgeText="Contattaci"
          className="text-center"
          title={
            <>
              Richiedi una{' '}
              <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary hidden md:flex">
                videochiamata dimostrativa
              </span>
              <span className="flex flex-col md:hidden">
                <span className="rounded-[2rem] rounded-tl-[8px] py-0 px-2 md:px-4 bg-secondary text-primary mb-1">
                  videochiamata
                </span>
                <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary text-center">
                  dimostrativa
                </span>
              </span>
            </>
          }
          subtitle="Compila il form per scoprire come Normo.ai può potenziare la tua
            attività professionale. Un nostro esperto ti contatterà entro 48h
            per fissare una videochiamata dimostrativa."
          titleAnimationStart="bottom"
        />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[912px] transition-opacity duration-1000 ease-in-out ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
            ref={formRef}
          >
            <FormField
              control={form.control}
              name="name"
              render={({field}) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isSubmitted}
                      placeholder="Mario"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="surname"
              render={({field}) => (
                <FormItem>
                  <FormLabel>Cognome</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isSubmitted}
                      placeholder="Rossi"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="company"
              render={({field}) => (
                <FormItem>
                  <FormLabel>Azienda</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isSubmitted}
                      placeholder="Studio Rossi & Associati"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>Azienda presso cui lavori</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({field}) => (
                <FormItem>
                  <FormLabel>Ruolo</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isSubmitted}
                      placeholder="Titolare"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Ruolo che ricopri presso l'azienda indicata
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({field}) => (
                <FormItem className="flex-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      disabled={isSubmitted}
                      placeholder="mario.rossi@normo.ai"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Indirizzo email dove vorresti essere contattato
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({field}) => (
                <FormItem className="flex-1">
                  <FormLabel>Telefono</FormLabel>
                  <FormControl>
                    <Input
                      type="phone"
                      disabled={isSubmitted}
                      placeholder="+393331234567"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Numero di telefono di riferimento
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="termsAndConditions"
              render={({field}) => (
                <FormItem className="md:col-span-2 flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      disabled={isSubmitted}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Accetto termini e condizioni</FormLabel>
                    <FormDescription>
                      Dichiaro di aver letto e compreso l'
                      <Link href="/privacy" className="underline">
                        informativa sulla privacy
                      </Link>{' '}
                      e acconsento al trattamento dei miei dati personali
                      secondo i&nbsp;
                      <Link href="/terms" className="underline">
                        termini
                      </Link>{' '}
                      stabiliti.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            {!isSubmitted ? (
              <div className="md:col-span-2 flex justify-center">
                <Button type="submit" className="flex-1">
                  <span className="pl-2">Invia richiesta</span>
                  {form.formState.isSubmitting ? (
                    <LoaderCircle
                      height={24}
                      width={24}
                      className="animate-spin"
                    />
                  ) : (
                    <Send height={24} width={24} />
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex justify-center items-center p-4 bg-green-200 border border-green-500 text-green-700 rounded text-lg md:col-span-2">
                Dati inviati con successo! Un nostro esperto ti contatterà entro
                48h.
              </div>
            )}
          </form>
        </Form>
      </div>
    </div>
  );
}
